// Simplified Chinese version of the landing page, ported from the developer
// handoff's main.js (27 Oct 2026).
//
// The page follows the visitor's browser language (Chinese when the first
// language is zh), and the footer's English / 中文 switch overrides that. The
// choice is saved under the app's own localStorage key, "locale", so it carries
// into the app, the login and the sign-up popups.
//
// Translations are in i18n-zh.json, keyed by the exact English text; anything
// not listed (brand, store and screen names) stays in English. If you change
// English copy on the page, change the matching key, or that line falls back
// to English. Keys starting with "@" translate aria-label, placeholder and
// title attributes.
//
// Scope: only text inside the landing page root is translated. The login and
// register popups live in App.vue and are translated by vue-i18n instead.
// In English nothing is walked or observed: the page is already English.

import { savedLocale, saveLocale } from "@/lang";

const MONTHS = {
  January: "1 月", February: "2 月", March: "3 月", April: "4 月", May: "5 月", June: "6 月",
  July: "7 月", August: "8 月", September: "9 月", October: "10 月", November: "11 月", December: "12 月",
};
const ZH_TITLE = "Adverpost | 适用于 LED 和 LCD 屏幕的数字标牌软件";
const ATTRS = ["aria-label", "placeholder", "title"];

const hour = (h, ap) => {
  h = +h;
  if (ap === "p" && h < 12) h += 12;
  if (ap === "a" && h === 12) h = 0;
  return h + " 时";
};

// Text that contains numbers, which a plain dictionary cannot hold.
const rulesFor = (dict) => [
  [/^(\d+) sec$/, (m) => m[1] + " 秒"],
  [/^(\d+(?:\.\d+)?) months?$/, (m) => m[1] + " 个月"],
  [/^(\d+) screens? · (.+)$/, (m) => m[1] + " 块屏幕 · " + (dict[m[2]] || m[2])],
  [/^on (\w+) · (\d+) bookings$/, (m) => "较 " + (MONTHS[m[1]] || m[1]) + " · " + m[2] + " 笔预订"],
  [/^(\d+%?) at (\d+)(a|p)$/, (m) => hour(m[2], m[3]) + " · " + m[1]],
  [/^(\d+)(a|p)$/, (m) => hour(m[1], m[2])],
  [/^From (\S+) per second$/, (m) => "每秒 " + m[1] + " 起"],
  [/^Last seen (\d+) min ago$/, (m) => m[1] + " 分钟前在线"],
  [/^Record (\d+) of (\d+)$/, (m) => "第 " + m[1] + " 条，共 " + m[2] + " 条"],
  [/^(\d+) campaigns on this display$/, (m) => "此屏幕上有 " + m[1] + " 个广告活动"],
  [/^(\d+) listed$/, (m) => "已上架 " + m[1] + " 块"],
];

let dictionary = null;
const loadDictionary = () =>
  dictionary
    ? Promise.resolve(dictionary)
    : import(/* webpackChunkName: "landing-zh" */ "./i18n-zh.json").then((m) => {
        dictionary = m.default || m;
        return dictionary;
      });

function initialLocale() {
  const saved = savedLocale();
  if (saved) return saved;
  const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || "en"];
  return /^zh/i.test(langs[0] || "") ? "zh" : "en";
}

/**
 * Start translating `root`. `onSwitch(locale)` runs when the visitor uses the
 * footer switch (not for the automatic choice). Returns a teardown function.
 */
export function initLandingI18n(root, { onSwitch } = {}) {
  const html = document.documentElement;
  const previousLang = html.getAttribute("lang");
  const englishTitle = document.title;
  const original = new WeakMap(); // text node -> its English text
  let dict = {};
  let rules = [];
  let lang = "en";
  // Whether anything has been translated, i.e. whether English needs restoring.
  let translated = false;
  let destroyed = false;

  const translate = (text) => {
    if (dict[text] !== undefined) return dict[text];
    for (const [re, fn] of rules) {
      const m = text.match(re);
      if (m) return fn(m);
    }
    return null;
  };

  // The cheap checks (blank text, no translation) come before the DOM lookups.
  const skipped = (node) => {
    const parent = node.parentElement;
    return !parent || /^(SCRIPT|STYLE|TEXTAREA)$/.test(parent.tagName) || parent.closest("[data-no-i18n]");
  };

  const doText = (node) => {
    const src = original.has(node) ? original.get(node) : node.nodeValue;
    if (lang === "zh") {
      const key = src.replace(/\s+/g, " ").trim();
      if (!key) return;
      const zh = translate(key);
      if (zh === null || skipped(node)) return;
      if (!original.has(node)) original.set(node, src);
      const value = src.match(/^\s*/)[0] + zh + src.match(/\s*$/)[0];
      if (node.nodeValue !== value) node.nodeValue = value;
    } else if (original.has(node)) {
      if (node.nodeValue !== src) node.nodeValue = src;
      original.delete(node);
    }
  };

  const doElement = (el) => {
    ATTRS.forEach((a) => {
      if (!el.hasAttribute(a)) return;
      const keep = "data-en-" + a;
      if (lang === "zh") {
        const src = el.getAttribute(keep) || el.getAttribute(a);
        const zh = dict["@" + src];
        if (zh) {
          el.setAttribute(keep, src);
          el.setAttribute(a, zh);
        }
      } else if (el.hasAttribute(keep)) {
        el.setAttribute(a, el.getAttribute(keep));
        el.removeAttribute(keep);
      }
    });
  };

  const walk = (node) => {
    if (node.nodeType === 3) return doText(node);
    if (node.nodeType !== 1) return;
    doElement(node);
    const w = document.createTreeWalker(node, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    let n;
    while ((n = w.nextNode())) {
      if (n.nodeType === 3) doText(n);
      else doElement(n);
    }
  };

  // While the page is in Chinese, content added or changed later (Vue updates,
  // the mockups, map popups) is translated as it appears. The observer only
  // runs in Chinese. Its callback is a microtask, so it never overlaps our own
  // writes; the records those writes cause are dropped with takeRecords().
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.type === "characterData") {
        original.delete(m.target);
        doText(m.target);
      } else {
        m.addedNodes.forEach(walk);
      }
    });
    observer.takeRecords();
  });

  const buttons = () => Array.from(root.querySelectorAll("[data-lang]"));

  const apply = (l) => {
    lang = l === "zh" ? "zh" : "en";
    if (lang === "zh") {
      walk(root);
      translated = true;
      observer.takeRecords();
      observer.observe(root, { childList: true, subtree: true, characterData: true });
    } else {
      observer.disconnect();
      if (translated) walk(root); // put the English text back
      translated = false;
    }
    html.setAttribute("lang", lang === "zh" ? "zh-CN" : "en-AU");
    document.title = lang === "zh" ? ZH_TITLE : englishTitle;
    buttons().forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.lang === lang)));
  };

  const set = (l) => {
    if (l !== "zh") return apply("en");
    return loadDictionary().then((d) => {
      if (destroyed) return;
      dict = d;
      rules = rulesFor(d);
      apply("zh");
    });
  };

  const onClick = (e) => {
    const b = e.target.closest("[data-lang]");
    if (!b || !root.contains(b)) return;
    const l = b.dataset.lang === "zh" ? "zh" : "en";
    saveLocale(l);
    if (onSwitch) {
      try {
        onSwitch(l);
      } catch (err) {
        // The page still switches even if the app's text cannot.
      }
    }
    set(l);
  };

  root.addEventListener("click", onClick);
  set(initialLocale());

  return () => {
    destroyed = true;
    observer.disconnect();
    root.removeEventListener("click", onClick);
    // <html lang> and the tab title belong to the app outside this page.
    if (previousLang === null) html.removeAttribute("lang");
    else html.setAttribute("lang", previousLang);
    document.title = englishTitle;
  };
}
